import { Token, tokenize } from "./sample-filter/tokenize";

export type BinaryOp =
  | "and"
  | "or"
  | "=="
  | "!="
  | "<"
  | "<="
  | ">"
  | ">="
  | "~="
  | "+"
  | "-"
  | "*"
  | "/"
  | "^"
  | "mod";

export type FilterAst =
  | { kind: "var"; name: string }
  | { kind: "num"; value: number }
  | { kind: "str"; value: string }
  | { kind: "const"; name: "True" | "False" | "None" }
  | { kind: "binary"; op: BinaryOp; left: FilterAst; right: FilterAst }
  | { kind: "unary"; op: "not" | "-"; arg: FilterAst }
  | { kind: "call"; fn: string; args: FilterAst[] }
  | { kind: "in"; left: FilterAst; values: FilterAst[]; negated: boolean };

export interface ParseError {
  message: string;
  position: number;
}

class ParseFailure extends Error {
  constructor(
    message: string,
    readonly position: number
  ) {
    super(message);
  }
}

export interface ParseResult {
  ast: FilterAst | null;
  error: ParseError | null;
}

const RELATION_OPS: readonly BinaryOp[] = [
  "==",
  "!=",
  "<",
  "<=",
  ">",
  ">=",
  "~=",
];

const isRelationOp = (text: string): text is BinaryOp =>
  RELATION_OPS.some((op) => op === text);

const kBinaryOps: readonly BinaryOp[] = [
  ...RELATION_OPS,
  "and",
  "or",
  "+",
  "-",
  "*",
  "/",
  "^",
  "mod",
];

const asBinaryOp = (text: string): BinaryOp => {
  const op = kBinaryOps.find((candidate) => candidate === text);
  if (!op) throw new Error(`not a binary operator: ${text}`);
  return op;
};

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  private peek(offset = 0): Token | undefined {
    return this.tokens[this.pos + offset];
  }

  private eat(): Token | undefined {
    return this.tokens[this.pos++];
  }

  private match(type: string, text?: string): boolean {
    const t = this.peek();
    if (!t || t.type !== type) return false;
    if (text !== undefined && t.text !== text) return false;
    return true;
  }

  private consume(type: string, text?: string): Token {
    const t = this.peek();
    if (!t || t.type !== type || (text !== undefined && t.text !== text)) {
      throw this.error(
        `Expected ${text ?? type}${t ? `, got "${t.text}"` : ""}`
      );
    }
    return this.eat()!;
  }

  private error(message: string): ParseFailure {
    const at = this.peek();
    return new ParseFailure(message, at?.from ?? this.tokens.at(-1)?.to ?? 0);
  }

  parse(): FilterAst {
    const expr = this.parseOr();
    if (this.pos < this.tokens.length) {
      throw this.error(`Unexpected "${this.peek()!.text}"`);
    }
    return expr;
  }

  private parseOr(): FilterAst {
    let left = this.parseAnd();
    while (this.match("keyword", "or")) {
      this.eat();
      const right = this.parseAnd();
      left = { kind: "binary", op: "or", left, right };
    }
    return left;
  }

  private parseAnd(): FilterAst {
    let left = this.parseNot();
    while (this.match("keyword", "and")) {
      this.eat();
      const right = this.parseNot();
      left = { kind: "binary", op: "and", left, right };
    }
    return left;
  }

  private parseNot(): FilterAst {
    if (this.match("keyword", "not")) {
      this.eat();
      const arg = this.parseNot();
      return { kind: "unary", op: "not", arg };
    }
    return this.parseInOrRelation();
  }

  private parseInOrRelation(): FilterAst {
    const left = this.parseRelation();
    // postfix `in (a, b, c)` / `not in (a, b, c)`
    if (this.match("keyword", "in") || this.match("keyword", "not in")) {
      const negated = this.peek()!.text === "not in";
      this.eat();
      this.consume("miscOperator", "(");
      const values: FilterAst[] = [];
      if (!this.match("miscOperator", ")")) {
        values.push(this.parseOr());
        while (this.match("miscOperator", ",")) {
          this.eat();
          values.push(this.parseOr());
        }
      }
      this.consume("miscOperator", ")");
      return { kind: "in", left, values, negated };
    }
    return left;
  }

  private parseRelation(): FilterAst {
    const left = this.parseAdd();
    const t = this.peek();
    if (t && t.type === "relation" && isRelationOp(t.text)) {
      this.eat();
      const right = this.parseAdd();
      return { kind: "binary", op: t.text, left, right };
    }
    return left;
  }

  private parseAdd(): FilterAst {
    let left = this.parseMul();
    while (this.match("miscOperator", "+") || this.match("miscOperator", "-")) {
      const op = asBinaryOp(this.eat()!.text);
      const right = this.parseMul();
      left = { kind: "binary", op, left, right };
    }
    return left;
  }

  private parseMul(): FilterAst {
    let left = this.parsePow();
    while (
      this.match("miscOperator", "*") ||
      this.match("miscOperator", "/") ||
      this.match("keyword", "mod")
    ) {
      const op = asBinaryOp(this.eat()!.text);
      const right = this.parsePow();
      left = { kind: "binary", op, left, right };
    }
    return left;
  }

  private parsePow(): FilterAst {
    const left = this.parseUnary();
    if (this.match("miscOperator", "^")) {
      this.eat();
      // right-associative
      const right = this.parsePow();
      return { kind: "binary", op: "^", left, right };
    }
    return left;
  }

  private parseUnary(): FilterAst {
    if (this.match("miscOperator", "-")) {
      this.eat();
      const arg = this.parseUnary();
      return { kind: "unary", op: "-", arg };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): FilterAst {
    const t = this.peek();
    if (!t) throw this.error("Unexpected end of expression");

    if (t.type === "number") {
      this.eat();
      return { kind: "num", value: parseFloat(t.text) };
    }
    if (t.type === "string") {
      this.eat();
      // Strip surrounding quotes and reverse filtrex's two string
      // escapes (`\"` → `"`, `\\` → `\`). Keeps round-trip stable for
      // filter values that contain quotes or backslashes.
      const raw = t.text.slice(1, -1);
      return { kind: "str", value: raw.replace(/\\(["\\])/g, "$1") };
    }
    if (t.type === "unterminatedString") {
      throw this.error("Unterminated string literal");
    }
    if (t.type === "miscOperator" && t.text === "(") {
      this.eat();
      const inner = this.parseOr();
      this.consume("miscOperator", ")");
      return inner;
    }
    if (
      t.type === "variable" ||
      t.type === "sampleFunction" ||
      t.type === "mathFunction"
    ) {
      this.eat();
      const name = this.parseDottedTail(t.text);
      // Function call
      if (this.match("miscOperator", "(")) {
        this.eat();
        const args: FilterAst[] = [];
        if (!this.match("miscOperator", ")")) {
          args.push(this.parseOr());
          while (this.match("miscOperator", ",")) {
            this.eat();
            args.push(this.parseOr());
          }
        }
        this.consume("miscOperator", ")");
        return { kind: "call", fn: name, args };
      }
      // Pseudo-constants True/False/None are tokenized as variables.
      if (name === "True" || name === "False" || name === "None") {
        return { kind: "const", name };
      }
      return { kind: "var", name };
    }

    throw this.error(`Unexpected "${t.text}"`);
  }

  /** Consume `.ident` repeats and return the joined identifier. */
  private parseDottedTail(head: string): string {
    let name = head;
    while (
      this.match("miscOperator", ".") &&
      this.peek(1)?.type === "variable"
    ) {
      this.eat(); // dot
      name += "." + this.eat()!.text;
    }
    return name;
  }
}

/** Parse a filtrex expression into an AST. Returns `{ ast: null, error }`
 *  on failure — callers fall back gracefully without recovery. */
export function parseFilter(text: string): ParseResult {
  const trimmed = text.trim();
  if (trimmed === "") return { ast: null, error: null };

  const tokens = tokenize(text).filter(
    // The CodeMirror tokenizer doesn't emit whitespace as tokens, but it
    // does emit `null`-type slices for unrecognized characters; skip them
    // here too so the parser only sees recognized tokens.
    (t) => t.type !== "null"
  );
  const parser = new Parser(tokens);
  try {
    const ast = parser.parse();
    return { ast, error: null };
  } catch (e) {
    if (e instanceof ParseFailure) {
      return { ast: null, error: { message: e.message, position: e.position } };
    }
    return {
      ast: null,
      error: { message: String(e), position: 0 },
    };
  }
}
